// Explainer system (2026-07-06, Ben-approved design):
//   (a) <ExplainerButton surfaceKey="board:…"/> — one "(i)" in a board's
//       header, opening a right-side sheet: "How this works & who owns what".
//   (b) <FormExplainer surfaceKey="form:…"/> — one collapsible explainer at
//       the top of each major form.
//   (c) NO per-field info buttons — fields get required/optional markers and
//       a one-line hint under the label instead (progressive disclosure).
//
// Content is DB-backed (public.module_explainers, open-edit RLS) so the AI
// chat answers "how do I…" from the same source the UI renders. Graceful
// empty state with inline edit — anyone can add or fix an explainer.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Info, Pencil, X } from 'lucide-react';
import { supabase } from './supabase';
import { useAuth } from './auth';
import { Button } from './components';
import { overlayClickClose } from './overlay-close';

interface ExplainerRow {
  explainer_id: string;
  surface_key: string;
  title: string;
  body_md: string;
  owners_md: string;
  updated_by: string | null;
  updated_at: string;
}

// ----- data hook -------------------------------------------------------------

function useExplainer(surfaceKey: string, enabled: boolean) {
  const [row, setRow] = useState<ExplainerRow | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || loaded) return;
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('module_explainers')
        .select('*')
        .eq('surface_key', surfaceKey)
        .maybeSingle();
      if (cancelled) return;
      if (err) setError(err.message);
      else setRow((data ?? null) as ExplainerRow | null);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [surfaceKey, enabled, loaded]);

  const refresh = useCallback(() => setLoaded(false), []);
  return { row, loaded, error, refresh, setRow };
}

// ----- tiny markdown renderer ------------------------------------------------
// Deliberately minimal (no new deps): paragraphs, "- " bullets, "1. " numbered
// lists, **bold**, and *italic*. That covers the seeded content.

function inline(text: string, keyBase: string) {
  // **bold** then *italic* inside the remaining segments.
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith('*') && p.endsWith('*') && p.length > 2) {
      return <em key={`${keyBase}-${i}`}>{p.slice(1, -1)}</em>;
    }
    return <span key={`${keyBase}-${i}`}>{p}</span>;
  });
}

export function MiniMarkdown({ text }: { text: string }) {
  const blocks = text.replace(/\r\n/g, '\n').split(/\n\s*\n/);
  return (
    <div className="space-y-3 text-sm text-tcs-text-secondary">
      {blocks.map((block, bi) => {
        const lines = block.split('\n').filter((l) => l.trim().length > 0);
        if (lines.length === 0) return null;
        const isBullets = lines.every((l) => l.trim().startsWith('- '));
        const isNumbered = lines.every((l) => /^\s*\d+\.\s/.test(l));
        if (isBullets) {
          return (
            <ul key={bi} className="list-disc space-y-1 pl-5">
              {lines.map((l, li) => (
                <li key={li}>{inline(l.trim().slice(2), `${bi}-${li}`)}</li>
              ))}
            </ul>
          );
        }
        if (isNumbered) {
          return (
            <ol key={bi} className="list-decimal space-y-1 pl-5">
              {lines.map((l, li) => (
                <li key={li}>
                  {inline(l.replace(/^\s*\d+\.\s/, ''), `${bi}-${li}`)}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={bi} className="whitespace-pre-line">
            {lines.map((l, li) => (
              <span key={li}>
                {li > 0 && <br />}
                {inline(l, `${bi}-${li}`)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// ----- editor (shared by sheet + collapsible) --------------------------------

function ExplainerEditor({
  surfaceKey,
  row,
  onSaved,
  onCancel,
}: {
  surfaceKey: string;
  row: ExplainerRow | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { employee } = useAuth();
  const [title, setTitle] = useState(row?.title ?? '');
  const [body, setBody] = useState(row?.body_md ?? '');
  const [owners, setOwners] = useState(row?.owners_md ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      surface_key: surfaceKey,
      title: title.trim(),
      body_md: body,
      owners_md: owners,
      updated_by: employee?.employee_id ?? null,
      updated_at: new Date().toISOString(),
    };
    // UPDATE-first, INSERT-if-absent (module_explainers has open-edit RLS;
    // blind upsert would need a DELETE grant it doesn't have).
    const { data: upd, error: e1 } = await supabase
      .from('module_explainers')
      .update(payload)
      .eq('surface_key', surfaceKey)
      .select('explainer_id');
    if (e1) {
      setSaving(false);
      setError(e1.message);
      return;
    }
    if ((upd ?? []).length === 0) {
      const { error: e2 } = await supabase
        .from('module_explainers')
        .insert(payload);
      if (e2) {
        setSaving(false);
        setError(e2.message);
        return;
      }
    }
    setSaving(false);
    onSaved();
  }

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Title <span className="text-rag-red">*</span>
        </span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="h-10 w-full rounded-md border border-tcs-border-strong bg-tcs-bg px-3 text-sm focus:border-tcs-accent focus:outline-none"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          How this works (markdown)
        </span>
        <textarea
          rows={10}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full resize-y rounded-md border border-tcs-border-strong bg-tcs-bg p-3 text-sm font-mono focus:border-tcs-accent focus:outline-none"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
          Who owns what (markdown)
        </span>
        <textarea
          rows={5}
          value={owners}
          onChange={(e) => setOwners(e.target.value)}
          className="w-full resize-y rounded-md border border-tcs-border-strong bg-tcs-bg p-3 text-sm font-mono focus:border-tcs-accent focus:outline-none"
        />
      </label>
      {error && (
        <div className="rounded-md border border-rag-red/40 bg-rag-red/10 p-2 text-xs text-rag-red">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

// ----- shared content block --------------------------------------------------

function ExplainerContent({
  surfaceKey,
  row,
  loaded,
  error,
  onChanged,
}: {
  surfaceKey: string;
  row: ExplainerRow | null;
  loaded: boolean;
  error: string | null;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);

  if (!loaded) {
    return <p className="text-sm text-tcs-text-muted">Loading…</p>;
  }
  if (error) {
    return <p className="text-sm text-rag-red">{error}</p>;
  }
  if (editing || !row) {
    return row || editing ? (
      <ExplainerEditor
        surfaceKey={surfaceKey}
        row={row}
        onSaved={() => {
          setEditing(false);
          onChanged();
        }}
        onCancel={() => setEditing(false)}
      />
    ) : (
      <div className="rounded-xl border border-dashed border-tcs-border bg-tcs-surface p-4">
        <p className="text-sm text-tcs-text-muted">
          No explainer yet for this surface.
        </p>
        <Button size="sm" className="mt-3" onClick={() => setEditing(true)}>
          Add one
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <MiniMarkdown text={row.body_md} />
      {row.owners_md.trim().length > 0 && (
        <div>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
            Who owns what
          </h4>
          <MiniMarkdown text={row.owners_md} />
        </div>
      )}
      <div className="flex items-center justify-between border-t border-tcs-border pt-3">
        <p className="text-[11px] text-tcs-text-muted">
          {row.updated_by ? `Updated by ${row.updated_by}` : 'Seeded'} ·{' '}
          {new Date(row.updated_at).toLocaleDateString()}
        </p>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 text-xs text-tcs-text-secondary hover:text-tcs-text"
        >
          <Pencil className="h-3 w-3" /> Edit
        </button>
      </div>
    </div>
  );
}

// ----- (a) board-header button + right-side sheet ----------------------------

export function ExplainerButton({ surfaceKey }: { surfaceKey: string }) {
  const [open, setOpen] = useState(false);
  const { row, loaded, error, refresh } = useExplainer(surfaceKey, open);

  return (
    <>
      <button
        type="button"
        aria-label="How this works & who owns what"
        title="How this works & who owns what"
        onClick={() => setOpen(true)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-tcs-border text-tcs-text-secondary hover:bg-tcs-surface hover:text-tcs-text"
      >
        <Info className="h-4 w-4" />
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-tcs-scrim/60"
          role="dialog"
          aria-modal="true"
          aria-label="How this works & who owns what"
          {...overlayClickClose(() => setOpen(false))}
        >
          <div
            className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-tcs-bg p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-2">
              <h2 className="text-base font-semibold text-tcs-text">
                {row?.title ?? 'How this works & who owns what'}
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ExplainerContent
              surfaceKey={surfaceKey}
              row={row}
              loaded={loaded}
              error={error}
              onChanged={refresh}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ----- (b) collapsible form-top explainer ------------------------------------

export function FormExplainer({ surfaceKey }: { surfaceKey: string }) {
  const [open, setOpen] = useState(false);
  const { row, loaded, error, refresh } = useExplainer(surfaceKey, open);

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-tcs-text-secondary hover:text-tcs-text"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <Info className="h-4 w-4 shrink-0" />
        <span className="font-medium">How this works &amp; who owns what</span>
      </button>
      {open && (
        <div className="border-t border-tcs-border px-4 py-3">
          <ExplainerContent
            surfaceKey={surfaceKey}
            row={row}
            loaded={loaded}
            error={error}
            onChanged={refresh}
          />
        </div>
      )}
    </div>
  );
}
